import React from "react";
import {
  Box,
  Heading,
  Text,
  TableContainer,
  useColorModeValue,
} from "@chakra-ui/react";

/**
 * Shared tokens for tables — matches Portfolio “Transactions” look (sticky header, row hover).
 */
export function useDashboardTableStyles() {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tableHeaderBg = useColorModeValue("gray.100", "gray.600");
  const neutralRowBg = useColorModeValue("white", "gray.800");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");
  const headerStripBg = useColorModeValue("gray.100", "gray.700");
  const headingColor = useColorModeValue("gray.800", "gray.100");

  const thProps = {
    py: 3,
    fontSize: "xs",
    fontWeight: "semibold",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "gray.600",
    borderBottomWidth: "2px",
    borderColor,
    sx: { _dark: { color: "gray.300" } },
  };

  const trHoverSx = {
    verticalAlign: "top",
    bg: neutralRowBg,
    transition: "background 0.15s ease, box-shadow 0.15s ease",
    cursor: "default",
    _hover: {
      bg: rowHoverBg,
      boxShadow: "inset 0 0 0 1px var(--chakra-colors-blackAlpha-50)",
      "& > td": { bg: rowHoverBg },
    },
    "& > td": {
      transition: "background 0.15s ease",
      borderBottomWidth: "1px",
      borderColor,
    },
  };

  return {
    borderColor,
    tableHeaderBg,
    neutralRowBg,
    rowHoverBg,
    cardBg,
    headerStripBg,
    headingColor,
    thProps,
    trHoverSx,
  };
}

/**
 * Card + scrollable area for a Chakra `<Table>`. Pass your full `<Table>...</Table>` as children.
 */
export function DashboardDataTable({
  title,
  description,
  children,
  maxH = { base: "none", md: "min(60vh, 480px)" },
  overflowY = { base: "visible", md: "auto" },
  /** neutral = gray header; emphasis = violet strip for “your data / notes / ledger” */
  variant = "neutral",
}) {
  const {
    borderColor,
    cardBg,
    headerStripBg,
    headingColor,
  } = useDashboardTableStyles();
  const emphasisHeaderBg = useColorModeValue("purple.50", "rgba(147, 51, 234, 0.14)");
  const emphasisHeading = useColorModeValue("purple.800", "purple.200");
  const emphasisLeft = useColorModeValue("purple.400", "purple.300");

  const headerBg = variant === "emphasis" ? emphasisHeaderBg : headerStripBg;
  const titleColor = variant === "emphasis" ? emphasisHeading : headingColor;

  return (
    <Box
      w="full"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      boxShadow="md"
      bg={cardBg}
      borderLeftWidth={variant === "emphasis" ? "4px" : "1px"}
      borderLeftColor={variant === "emphasis" ? emphasisLeft : borderColor}
    >
      {(title || description) && (
        <Box
          px={{ base: 4, md: 6 }}
          py={4}
          borderBottomWidth="1px"
          borderColor={borderColor}
          bg={headerBg}
        >
          {title && (
            <Heading
              size="sm"
              textAlign="left"
              color={titleColor}
              fontWeight="bold"
            >
              {title}
            </Heading>
          )}
          {description && (
            <Text
              fontSize="sm"
              color="gray.500"
              mt={title ? 1 : 0}
              textAlign="left"
            >
              {description}
            </Text>
          )}
        </Box>
      )}
      <TableContainer
        maxH={maxH}
        overflowY={overflowY}
        overflowX="auto"
        borderTopWidth={title || description ? "0" : undefined}
        sx={{ scrollbarGutter: "stable" }}
      >
        {children}
      </TableContainer>
    </Box>
  );
}
